import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { ArrowLeft, Pencil, Plus, Trash2 } from 'lucide-react';

import {
  useCreateInternalAITaskModel,
  useDeleteInternalAITaskModel,
  useInternalAITask,
  useUpdateInternalAITaskModel,
} from '../../services/internal-ai-task.service';
import { useLLMModels } from '../../services/llm-catalog.service';
import {
  InternalAITaskModel,
  InternalAITaskModelFormValues,
  LLMModel,
  ParamOverrides,
} from '../../types/admin';
import { FormattedNumberTextField } from '../../components/common/FormattedNumberTextField';
import { ParamOverridesFields } from '../../components/admin/ParamOverridesFields';
import { BillingModeNote } from '../../components/admin/internal-ai-task/BillingModeNote';
import {
  formatCatalogModelOption,
  getCatalogModelOptionParts,
} from '../../utils/llmCatalogModelDisplay';

function effectiveParamOverrides(model: InternalAITaskModel): ParamOverrides | null {
  const merged: ParamOverrides = { ...(model.paramOverrides ?? {}) };
  if (merged.temperature === undefined && model.temperature != null) {
    merged.temperature = model.temperature;
  }
  if (merged.maxOutputTokens === undefined && model.maxTokens != null) {
    merged.maxOutputTokens = model.maxTokens;
  }
  return Object.keys(merged).length > 0 ? merged : null;
}

function formatParamOverrides(value: ParamOverrides | null): string {
  if (!value) return '-';
  return [
    value.temperature !== undefined ? `temp ${value.temperature}` : null,
    value.maxOutputTokens !== undefined ? `max ${value.maxOutputTokens}` : null,
    value.topP !== undefined ? `topP ${value.topP}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

interface ModelFormDialogProps {
  open: boolean;
  onClose: () => void;
  taskPublicId: string;
  model?: InternalAITaskModel | null;
}

type ErrorKey = keyof InternalAITaskModelFormValues | 'catalogModel';

const emptyValues: InternalAITaskModelFormValues = {
  llmModelPublicId: '',
  modelName: '',
  weight: 1,
  priority: 1,
  paramOverrides: null,
  config: '',
};

const ModelFormDialog: React.FC<ModelFormDialogProps> = ({
  open,
  onClose,
  taskPublicId,
  model,
}) => {
  const isEditMode = !!model;
  const createMutation = useCreateInternalAITaskModel();
  const updateMutation = useUpdateInternalAITaskModel();
  const { data: catalogModels = [], isLoading: isLoadingCatalog } = useLLMModels({
    isActive: true,
    all: true,
    page: 1,
    limit: 100,
  });

  const [values, setValues] = useState<InternalAITaskModelFormValues>(emptyValues);
  const [errors, setErrors] = useState<Partial<Record<ErrorKey, string>>>({});

  const selectedCatalogModel = useMemo(
    () => catalogModels.find((item) => item.publicId === values.llmModelPublicId) ?? null,
    [catalogModels, values.llmModelPublicId]
  );

  React.useEffect(() => {
    if (model) {
      setValues({
        llmModelPublicId: model.llmModelPublicId ?? '',
        modelName: model.modelName ?? '',
        weight: model.weight,
        priority: model.priority,
        paramOverrides: effectiveParamOverrides(model),
        config: model.config ?? '',
      });
    } else {
      setValues(emptyValues);
    }
    setErrors({});
  }, [model, open]);

  const validate = () => {
    const nextErrors: Partial<Record<ErrorKey, string>> = {};

    if (!values.llmModelPublicId) {
      nextErrors.catalogModel = 'Select an LLM model';
    }

    if (!Number.isInteger(values.priority) || values.priority < 1) {
      nextErrors.priority = 'Priority must be at least 1';
    }

    if (!Number.isInteger(values.weight) || values.weight < 1) {
      nextErrors.weight = 'Weight must be at least 1';
    }

    const temperature = values.paramOverrides?.temperature;
    if (temperature !== undefined && (temperature < 0 || temperature > 2)) {
      nextErrors.paramOverrides = 'Temperature must be between 0 and 2';
    }

    const topP = values.paramOverrides?.topP;
    if (topP !== undefined && (topP < 0 || topP > 1)) {
      nextErrors.paramOverrides = 'Top P must be between 0 and 1';
    }

    const maxOutputTokens = values.paramOverrides?.maxOutputTokens;
    if (
      maxOutputTokens !== undefined &&
      (!Number.isInteger(maxOutputTokens) || maxOutputTokens < 1)
    ) {
      nextErrors.paramOverrides = 'Max output tokens must be a positive integer';
    }

    if (values.config?.trim()) {
      try {
        JSON.parse(values.config);
      } catch {
        nextErrors.config = 'Config must be valid JSON';
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      llmModelPublicId: values.llmModelPublicId,
      modelName: (selectedCatalogModel?.code ?? values.modelName?.trim()) || undefined,
      weight: values.weight,
      priority: values.priority,
      paramOverrides: values.paramOverrides ?? (isEditMode ? null : undefined),
      // Legacy columns are folded into paramOverrides on load; clear them so a cleared field
      // does not come back from the old alias.
      temperature: isEditMode ? null : undefined,
      maxTokens: isEditMode ? null : undefined,
      config: values.config?.trim() || undefined,
    };

    if (isEditMode && model) {
      await updateMutation.mutateAsync({
        publicId: model.publicId,
        ...payload,
      });
    } else {
      await createMutation.mutateAsync({
        taskPublicId,
        ...payload,
      });
    }

    onClose();
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit LLM Model Mapping' : 'Assign LLM Model'}</DialogTitle>
      <DialogContent>
        <Box className="mt-4 space-y-4">
          <Autocomplete
            fullWidth
            options={catalogModels}
            value={selectedCatalogModel}
            loading={isLoadingCatalog}
            disabled={isLoadingCatalog || isSaving}
            getOptionLabel={formatCatalogModelOption}
            isOptionEqualToValue={(option, value) => option.publicId === value.publicId}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <CatalogModelOptionContent model={option} />
              </Box>
            )}
            openOnFocus
            autoHighlight
            onChange={(_event, nextModel) =>
              setValues((prev) => ({
                ...prev,
                llmModelPublicId: nextModel?.publicId ?? '',
                modelName: nextModel?.code ?? '',
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label="LLM Model"
                error={!!errors.catalogModel}
                helperText={errors.catalogModel}
              />
            )}
            noOptionsText="No LLM models found"
          />

          <Box className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormattedNumberTextField
              label="Priority"
              value={values.priority}
              error={!!errors.priority}
              helperText={errors.priority || 'Higher number is selected first'}
              onValueChange={(value) =>
                setValues((prev) => ({ ...prev, priority: Math.max(1, value ?? 1) }))
              }
            />
            <FormattedNumberTextField
              label="Weight"
              value={values.weight}
              error={!!errors.weight}
              helperText={errors.weight || 'Weighted choice within same priority'}
              onValueChange={(value) =>
                setValues((prev) => ({ ...prev, weight: Math.max(1, value ?? 1) }))
              }
            />
          </Box>

          <ParamOverridesFields
            value={values.paramOverrides}
            onChange={(next) => setValues((prev) => ({ ...prev, paramOverrides: next }))}
            modelConfig={selectedCatalogModel?.config}
            disabled={isSaving}
          />
          {errors.paramOverrides && <Alert severity="error">{errors.paramOverrides}</Alert>}

          <TextField
            fullWidth
            label="Config JSON"
            value={values.config ?? ''}
            multiline
            minRows={3}
            error={!!errors.config}
            helperText={errors.config || 'Optional task-model config override'}
            onChange={(event) => setValues((prev) => ({ ...prev, config: event.target.value }))}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isSaving}>
          {isEditMode ? 'Update' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const InternalAITaskDetail: React.FC = () => {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();
  const { data: task, isLoading, isError } = useInternalAITask(publicId);
  const { data: catalogModels = [] } = useLLMModels({
    isActive: true,
    all: true,
    page: 1,
    limit: 100,
  });
  const deleteMutation = useDeleteInternalAITaskModel();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<InternalAITaskModel | null>(null);

  const modelByPublicId = useMemo(() => {
    const map = new Map<string, LLMModel>();
    catalogModels.forEach((item) => map.set(item.publicId, item));
    return map;
  }, [catalogModels]);

  const handleAdd = () => {
    setEditingModel(null);
    setDialogOpen(true);
  };

  const handleEdit = (model: InternalAITaskModel) => {
    setEditingModel(model);
    setDialogOpen(true);
  };

  const handleDelete = async (model: InternalAITaskModel) => {
    await deleteMutation.mutateAsync(model.publicId);
  };

  const columns: GridColDef[] = [
    {
      field: 'model',
      headerName: 'Model',
      flex: 1,
      minWidth: 260,
      valueGetter: (_value, row: InternalAITaskModel) => {
        const catalogModel = row.llmModelPublicId
          ? modelByPublicId.get(row.llmModelPublicId)
          : undefined;
        return catalogModel
          ? formatCatalogModelOption(catalogModel)
          : row.modelName || 'Unknown model';
      },
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 110,
      align: 'center',
    },
    {
      field: 'weight',
      headerName: 'Weight',
      width: 110,
      align: 'center',
    },
    {
      field: 'paramOverrides',
      headerName: 'Overrides',
      width: 220,
      sortable: false,
      valueGetter: (_value, row) => formatParamOverrides(effectiveParamOverrides(row)),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box className="flex gap-1">
          <IconButton size="small" onClick={() => handleEdit(params.row)}>
            <Pencil size={18} />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            disabled={deleteMutation.isPending}
            onClick={() => handleDelete(params.row)}
          >
            <Trash2 size={18} />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (isLoading) {
    return (
      <Box className="flex min-h-[320px] items-center justify-center">
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !task) {
    return (
      <Box className="p-6">
        <Alert severity="error">Internal AI task not found.</Alert>
      </Box>
    );
  }

  return (
    <Box className="p-6">
      <Box className="mb-6 flex items-center justify-between gap-4">
        <Box className="flex items-center gap-3">
          <IconButton onClick={() => navigate('/internal-ai-tasks')}>
            <ArrowLeft size={20} />
          </IconButton>
          <Box>
            <Box className="flex items-center gap-2">
              <Typography variant="h4" className="font-bold text-gray-900 dark:text-slate-100">
                {task.name}
              </Typography>
              <Chip
                size="small"
                label={task.enabled ? 'Enabled' : 'Disabled'}
                color={task.enabled ? 'success' : 'default'}
              />
            </Box>
            <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
              {task.taskKey}
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<Plus size={18} />} onClick={handleAdd}>
          Assign Model
        </Button>
      </Box>

      <Box className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <Box>
          <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
            Billing Mode
          </Typography>
          <Typography>{task.billingMode}</Typography>
          <BillingModeNote mode={task.billingMode} variant="body" />
        </Box>
        <Box>
          <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
            Token Limit
          </Typography>
          <Typography>{task.tokenLimit ?? '-'}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
            Timeout
          </Typography>
          <Typography>{task.timeoutMs ? `${task.timeoutMs} ms` : '-'}</Typography>
        </Box>
      </Box>

      {task.models.length === 0 && (
        <Alert severity="warning" className="mb-4">
          This task has no assigned LLM model. It cannot execute until at least one model is
          assigned.
        </Alert>
      )}

      <Box className="rounded-xl bg-white shadow-sm dark:bg-slate-800">
        <DataGrid
          rows={task.models}
          columns={columns}
          autoHeight
          disableRowSelectionOnClick
          getRowId={(row) => row.publicId}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          className="border-none"
        />
      </Box>

      <ModelFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        taskPublicId={task.publicId}
        model={editingModel}
      />
    </Box>
  );
};

const CatalogModelOptionContent: React.FC<{ model: LLMModel }> = ({ model }) => {
  const { providerModel, priceLabel, taskTypeLabel } = getCatalogModelOptionParts(model);

  return (
    <Box
      className="grid w-full items-center gap-3 font-mono text-sm"
      sx={{ gridTemplateColumns: 'minmax(220px, 1.6fr) minmax(150px, 1fr) minmax(130px, 1fr)' }}
    >
      <Typography variant="body2" className="truncate font-mono">
        {providerModel}
      </Typography>
      <Typography variant="body2" className="truncate font-mono">
        {priceLabel}
      </Typography>
      <Typography variant="body2" className="truncate font-mono">
        {taskTypeLabel}
      </Typography>
    </Box>
  );
};
